import type { Lang } from '@/shared/domain/types';

/**
 * Mensajes tipados. Cada módulo define los suyos con `defineMessages` y los consume con `useT(def)`:
 *
 *   export const preciosMsgs = defineMessages('precios', { titulo: 'Precios' }, { titulo: 'Prices' });
 *   const { t } = useT(preciosMsgs);   //  t('titulo')  → las claves se autocompletan y se validan en compilación
 *
 * - `en` DEBE tener exactamente la misma forma que `es` (el compilador lo exige).
 * - Marcadores: "{nombre}". Plural simple: "un animal|{count} animales" (se elige con Intl.PluralRules
 *   cuando `params.count` es numérico).
 */
export type MessageTree = { readonly [key: string]: string | MessageTree };

export type SameShape<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : SameShape<T[K]>;
};

export interface MessageDef<E extends MessageTree = MessageTree> {
  readonly ns: string;
  readonly es: E;
  readonly en: SameShape<E>;
}

export function defineMessages<const E extends MessageTree>(
  ns: string,
  es: E,
  en: SameShape<E>,
): MessageDef<E> {
  return { ns, es, en };
}

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type MessageKey<E extends MessageTree> = Leaves<E>;

export type TParams = Record<string, string | number>;

const lookup = (tree: MessageTree, key: string): string | undefined => {
  let node: string | MessageTree | undefined = tree;
  for (const part of key.split('.')) {
    if (node === undefined || typeof node === 'string') return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
};

const pluralRules: Record<Lang, Intl.PluralRules> = {
  es: new Intl.PluralRules('es'),
  en: new Intl.PluralRules('en'),
};

/** Traduce sin hooks (servicios, notificaciones). Si falta la clave devuelve la propia clave. */
export function translate(def: MessageDef, lang: Lang, key: string, params?: TParams): string {
  let msg = lookup(def[lang], key) ?? lookup(def.es, key);
  if (msg === undefined) return key;
  if (msg.includes('|') && typeof params?.count === 'number') {
    const parts = msg.split('|');
    msg =
      (pluralRules[lang].select(params.count) === 'one' ? parts[0] : (parts[1] ?? parts[0])) ?? msg;
  }
  if (!params) return msg;
  return msg.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m,
  );
}
